import { useRef } from "react";

import { Spinner } from "@narmi/design_system";

import styles from "./ledgerColumns.module.scss";

import type { ReactLocalization } from "@fluent/react";

interface ReceiptCellProps {
  transaction: API.Transaction;
  l10n: ReactLocalization;
  canWrite: boolean;
  isUploading: boolean;
  acceptMimeTypes?: string[];
  onFileChosen: (transaction: API.Transaction, file: File) => void;
  onPreview: (transaction: API.Transaction) => void;
}

const ReceiptCell = ({
  transaction,
  l10n,
  canWrite,
  isUploading,
  acceptMimeTypes,
  onFileChosen,
  onPreview,
}: ReceiptCellProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  if (isUploading) {
    return (
      <span
        className={styles.receiptUploading}
        data-testid={`ledger-receipt-uploading-${transaction.id}`}
      >
        <Spinner size={20} strokeWidth={2} color="var(--theme-primary)" />
      </span>
    );
  }

  if (transaction.receipt) {
    return (
      <button
        type="button"
        className={styles.receiptIconButton}
        onClick={(event) => {
          event.stopPropagation();
          onPreview(transaction);
        }}
        aria-label={l10n.getString("live-ledger-receipt-view")}
        data-testid={`ledger-receipt-icon-${transaction.id}`}
      >
        <span className="narmi-icon-file" aria-hidden="true" />
      </button>
    );
  }

  // Pending rows have no settled_at and get an empty cell, and Viewers get one
  // too — they can open a receipt but not attach one.
  if (!canWrite || !transaction.settled_at) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={styles.receiptAddState}
        onClick={(event) => {
          event.stopPropagation();
          inputRef.current?.click();
        }}
        data-testid={`ledger-receipt-add-${transaction.id}`}
      >
        <span className="narmi-icon-plus" aria-hidden="true" />
        <span className={styles.receiptAddLabel}>
          {l10n.getString("live-ledger-receipt-add")}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={acceptMimeTypes?.join(",")}
        className={styles.receiptFileInput}
        data-testid={`ledger-receipt-input-${transaction.id}`}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          const input = event.target;
          const file = input.files?.[0];
          // Reset so choosing the same file twice still fires a change event.
          input.value = "";
          if (file) {
            onFileChosen(transaction, file);
          }
        }}
      />
    </>
  );
};

export default ReceiptCell;
